import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const AnnualDinnerRegistration = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    contactNumber: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const validate = () => {
    let tempErrors = {};
    if (!formData.firstName.trim()) tempErrors.firstName = "First Name is required";
    if (!formData.lastName.trim()) tempErrors.lastName = "Last Name is required";
    if (!formData.email.match(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/)) tempErrors.email = "Invalid email format";
    if (!formData.contactNumber.trim()) tempErrors.contactNumber = "Contact Number is required";
    if (!formData.message.trim()) tempErrors.message = "Message is required";

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: '' });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      setIsSubmitting(true);
      // Simulating API call
      setTimeout(() => {
        setIsSubmitting(false);
        setSuccessMsg("Registration successful! We look forward to seeing you.");
        setFormData({ firstName: '', lastName: '', email: '', contactNumber: '', message: '' });
        setTimeout(() => setSuccessMsg(''), 5000);
      }, 2000);
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* 1. HERO SECTION */}
      <section
        className="relative h-[45vh] flex items-center justify-center bg-center"
        style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.65), rgba(0,0,0,0.65)), url('/communitydinner.jpg')` }}
      >
        <div className="text-center text-white px-4">
          <motion.h1
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="text-4xl md:text-6xl font-serif font-black uppercase tracking-tight"
          >
            Register Now
          </motion.h1>
          <div className="h-1.5 w-20 bg-[#f9b50b] mx-auto mt-4"></div>

          <p className="mt-4 uppercase tracking-[0.3em] text-xs font-bold">
            <Link to="/" className="hover:text-[#f9b50b] text-gray-300 transition-colors">Home</Link>
            <span className="mx-2 text-gray-400">/</span>
            <Link to="/annual-dinner" className="hover:text-[#f9b50b] text-gray-300 transition-colors">Annual Dinner</Link>
            <span className="mx-2 text-gray-400">/</span>
            <span className="text-white">Register</span>
          </p>
        </div>
      </section>

      {/* 2. MAIN CONTENT WRAPPER */}
      <div className="max-w-4xl mx-auto px-6 py-16 mt-10">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#221f1f] uppercase leading-tight mb-4">
            Annual Dinner <span className="text-[#f9b50b]">Registration</span>
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-sm md:text-base italic">
            Please fill out the form below to secure your spot. All fields are mandatory.
          </p>
        </div>

        <div className="bg-gray-50 p-8 md:p-12 rounded-2xl shadow-lg border border-gray-100">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* First Name */}
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest text-[#221f1f]">First Name</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="John"
                  className={`w-full p-4 bg-white border ${errors.firstName ? 'border-red-500' : 'border-gray-200'} focus:outline-none focus:border-[#f9b50b] transition-colors text-sm rounded`}
                />
                {errors.firstName && <p className="text-red-500 text-[10px] uppercase font-bold">{errors.firstName}</p>}
              </div>

              {/* Last Name */}
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest text-[#221f1f]">Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Doe"
                  className={`w-full p-4 bg-white border ${errors.lastName ? 'border-red-500' : 'border-gray-200'} focus:outline-none focus:border-[#f9b50b] transition-colors text-sm rounded`}
                />
                {errors.lastName && <p className="text-red-500 text-[10px] uppercase font-bold">{errors.lastName}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Email */}
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest text-[#221f1f]">Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="john@example.com"
                  className={`w-full p-4 bg-white border ${errors.email ? 'border-red-500' : 'border-gray-200'} focus:outline-none focus:border-[#f9b50b] transition-colors text-sm rounded`}
                />
                {errors.email && <p className="text-red-500 text-[10px] uppercase font-bold">{errors.email}</p>}
              </div>

              {/* Contact Number */}
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest text-[#221f1f]">Contact Number</label>
                <input
                  type="tel"
                  name="contactNumber"
                  value={formData.contactNumber}
                  onChange={handleChange}
                  placeholder="+1 (555) 000-0000"
                  className={`w-full p-4 bg-white border ${errors.contactNumber ? 'border-red-500' : 'border-gray-200'} focus:outline-none focus:border-[#f9b50b] transition-colors text-sm rounded`}
                />
                {errors.contactNumber && <p className="text-red-500 text-[10px] uppercase font-bold">{errors.contactNumber}</p>}
              </div>
            </div>

            {/* Message */}
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-widest text-[#221f1f]">Message</label>
              <textarea
                name="message"
                rows="5"
                value={formData.message}
                onChange={handleChange}
                placeholder="Any dietary restrictions or special requests?"
                className={`w-full p-4 bg-white border ${errors.message ? 'border-red-500' : 'border-gray-200'} focus:outline-none focus:border-[#f9b50b] transition-colors text-sm rounded`}
              ></textarea>
              {errors.message && <p className="text-red-500 text-[10px] uppercase font-bold">{errors.message}</p>}
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <button
                disabled={isSubmitting}
                className="w-full bg-[#f9b50b] text-[#221f1f] font-bold uppercase tracking-widest py-4 hover:bg-[#221f1f] hover:text-white transition-all duration-300 shadow-xl text-sm rounded disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Processing..." : "Complete Registration"}
              </button>
            </div>

            {successMsg && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6 p-4 bg-green-50 border border-green-200 text-center rounded"
              >
                <p className="text-green-800 font-bold text-sm uppercase tracking-tight">
                  {successMsg}
                </p>
              </motion.div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default AnnualDinnerRegistration;
